import React from 'react';
import { Badge } from '../../design-system/Badge';
import { Calendar, CheckSquare, Layers } from 'lucide-react';

export const TaskCard = ({ task, onClick, onDragStart }) => {
  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'urgent':
        return <Badge variant="danger" size="xs">Urgent</Badge>;
      case 'high':
        return <Badge variant="warning" size="xs">High</Badge>;
      case 'medium':
        return <Badge variant="info" size="xs">Medium</Badge>;
      case 'low':
      default:
        return <Badge variant="neutral" size="xs">Low</Badge>;
    }
  };

  const checklistTotal = task.checklists?.length || 0;
  const checklistDone = task.checklists?.filter((c) => c.done).length || 0;

  const subtasksTotal = task.subtasks?.length || 0;
  const subtasksDone = task.subtasks?.filter((s) => s.done).length || 0;

  const assigneeAvatar = typeof task.assignee === 'object' ? task.assignee?.avatar : null;
  const assigneeName = typeof task.assignee === 'object' ? task.assignee?.name : task.assignee;

  return (
    <div
      onClick={onClick}
      draggable={!!onDragStart}
      onDragStart={(e) => onDragStart && onDragStart(e, task.id)}
      style={{
        backgroundColor: 'var(--surface-card)',
        boxShadow: 'var(--shadow-raised-sm)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        padding: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        cursor: 'grab',
        transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
        userSelect: 'none'
      }}
      className="interactive-raised"
    >
      {/* Top Header: ID & Priority */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-muted)', letterSpacing: '0.04em' }}>
          {task.id}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {task.estimate && (
            <span style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-muted)', backgroundColor: 'var(--surface-recessed)', padding: '1px 5px', borderRadius: '3px' }}>
              {task.estimate}
            </span>
          )}
          {getPriorityBadge(task.priority)}
        </div>
      </div>

      {/* Task Title */}
      <h4
        style={{
          fontSize: '14px',
          fontWeight: '600',
          color: 'var(--text-primary)',
          lineHeight: '1.4',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}
      >
        {task.title}
      </h4>

      {/* Labels */}
      {task.labels && task.labels.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {task.labels.map((lbl) => (
            <Badge key={lbl} size="xs" variant="recessed">
              {lbl}
            </Badge>
          ))}
        </div>
      )}

      {/* Footer: Date, Checklist, Subtasks count, Assignee Avatar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: '8px',
          borderTop: '1px solid var(--border-divider)',
          marginTop: '2px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11px', color: 'var(--text-muted)' }}>
          {task.dueDate && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Calendar size={12} />
              {task.dueDate.split('-').slice(1).join('/')}
            </span>
          )}
          {checklistTotal > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }} title={`${checklistDone} of ${checklistTotal} checklists completed`}>
              <CheckSquare size={12} />
              {checklistDone}/{checklistTotal}
            </span>
          )}
          {subtasksTotal > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }} title={`${subtasksDone} of ${subtasksTotal} subtasks completed`}>
              <Layers size={12} />
              {subtasksDone}/{subtasksTotal}
            </span>
          )}
        </div>

        {assigneeAvatar ? (
          <img
            src={assigneeAvatar}
            alt={assigneeName || 'Assignee'}
            title={`Assigned to ${assigneeName}`}
            style={{
              width: '22px',
              height: '22px',
              borderRadius: '999px',
              objectFit: 'cover',
              border: '1px solid var(--border-subtle)'
            }}
          />
        ) : assigneeName ? (
          <div
            title={`Assigned to ${assigneeName}`}
            style={{
              width: '22px',
              height: '22px',
              borderRadius: '999px',
              backgroundColor: 'var(--surface-recessed)',
              color: 'var(--accent-teal)',
              fontSize: '10px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid var(--border-subtle)'
            }}
          >
            {assigneeName.substring(0, 1).toUpperCase()}
          </div>
        ) : null}
      </div>
    </div>
  );
};
